import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProductHost, type SectionKey } from './host';
import { YtBg, YtPlayer, isVideo, ytId, ytThumb } from './media';
import type { Product, ProductReview } from './types';
import './product-v2.css';
import { SetBox, SetDayNight, SetGift, SetSavings, SetStory } from './SetSections';

// New-design product page (shown when useSiteDesign().isNew). Layout and
// styles come from the approved Booster draft. Shared content (images,
// claims, how-to-use, ingredients, skin guide, FAQs) comes from the
// product's usual fields; the new-design-only sections read
// product.designV2 (rxk_product_content.design_v2), edited under
// Admin > Products > New design. Any section without content is hidden.
// design_v2 can also override shared fields (name, gallery, claims, FAQs...)
// so the new page can carry the draft copy without changing the classic one.

const SKIN_PHOTO: Record<string, string> = {
  dry: '/v2/p/skin-normal.jpg',
  oily: '/v2/p/skin-oily.jpg',
  sensitive: '/v2/p/skin-dry.jpg',
  combination: '/v2/p/skin-dull.jpg',
  normal: '/v2/p/skin-normal.jpg',
  'acne-prone': '/v2/serum-c1.jpg',
  mature: '/v2/cream-c4.jpg',
};
const STEP_TAG: Record<string, string> = { OPEN: 'STEP 01 · OPEN', TREAT: 'STEP 02 · TREAT', LOCK: 'STEP 03 · LOCK', SET: 'OPEN · TREAT · LOCK' };
const STEPS = ['OPEN', 'TREAT', 'LOCK'];

function Svg({ d, box = '0 0 24 24' }: { d: string; box?: string }) {
  return (
    <svg viewBox={box} aria-hidden="true">
      {d.split('|').map((p, i) =>
        p.startsWith('c:') ? (
          <circle
            key={i}
            {...Object.fromEntries(
              p
                .slice(2)
                .split(',')
                .map((kv) => kv.split('=')),
            )}
          />
        ) : (
          <path key={i} d={p} />
        ),
      )}
    </svg>
  );
}
const ICON = {
  q: 'c:cx=12,cy=12,r=9|M9.6 9.3a2.4 2.4 0 1 1 3.3 2.2c-.6.3-.9.8-.9 1.5v.4|M12 16.6v.1',
  h: 'M12 3.5l1.9 5 5 1.9-5 1.9-1.9 5-1.9-5-5-1.9 5-1.9z|M18.5 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z',
  ok: 'M5 12.5l4.5 4.5L19 7.5',
  bag: 'M5 8h14l-1 12H6z|M9 8V6a3 3 0 0 1 6 0v2',
  shield: 'M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z|M9 12l2 2 4-4',
  play: 'M7 5l12 7-12 7z',
  share: 'c:cx=18,cy=5,r=2.5|c:cx=6,cy=12,r=2.5|c:cx=18,cy=19,r=2.5|M8.2 10.8l7.6-4.4M8.2 13.2l7.6 4.4',
  check: 'M5 12l5 5 9-10',
};
const TRUST: [string, string][] = [
  ['M4 20V9l8-5 8 5v11z|M9 20v-6h6v6', 'GMP certified manufacturing'],
  ['M9 3h6v5l4 10a2 2 0 0 1-2 3H7a2 2 0 0 1-2-3l4-10z|M8 14h8', 'Pharmacist verified formula'],
  ['M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z|M9 12l2 2 4-4', 'BSTI certified'],
  ['c:cx=12,cy=12,r=9|M3 12h18M12 3c3 3.5 3 14.5 0 18M12 3c-3 3.5-3 14.5 0 18', 'Made in Korea'],
];
const REVIEW_SKINS = ['Oily', 'Dry', 'Combination', 'Sensitive'];
const pct = (r: number) => `${((r / 5) * 100).toFixed(1)}%`;
const lines = (s: string) => s.split('\n').flatMap((l, i) => (i ? [<br key={i} />, l] : [l]));
const STEP_ICONS = [
  'M16 4c4 5.5 7 9.4 7 13a7 7 0 0 1-14 0c0-3.6 3-7.5 7-13z|M12.5 18.5a3.5 3.5 0 0 0 3.5 3.5|M25 5v3M23.5 6.5h3',
  'M12 13h8v14a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2z|M14 13V9h4v4|M14 9V6h7v3|M21 7.5h3',
  'M11 28v-9l-3-4a2 2 0 0 1 3.2-2.4L13 15V6a2 2 0 0 1 4 0v8|M17 13V5a2 2 0 0 1 4 0v9|M21 12a2 2 0 0 1 4 0v8c0 4.5-3 8-7 8h-7',
  'M13 3h6v4h-6z|M14 7v5l-4 4v11a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V16l-4-4V7|M10 20h12',
];

export default function ProductV2({
  product,
  outOfStock,
  reviews,
  onAddToCart,
}: {
  product: Product;
  outOfStock: boolean;
  reviews: ProductReview[];
  onAddToCart: (qty: number) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Site-specific links, catalog, account and cart step (see ./host).
  const host = useProductHost();
  const { products, routineSet, links } = host;
  const v = product.designV2 ?? {};
  // The 3-Step Set uses this page too, with its own sections swapped in.
  const isSet = product.stepKeyword === 'SET';
  const root = useRef<HTMLDivElement>(null);

  const showToast = host.toast;
  // A host (Pick.K Seoul seller shops) may hide sections.
  const on = (k: SectionKey) => !host.hidden?.includes(k);
  const { currentUser, isPreview } = host;
  const name = v.name || product.name;
  const photos = v.gallery?.length ? v.gallery.filter((g) => g.src) : [product.image, ...product.galleryImages].filter(Boolean).map((src) => ({ src, poster: undefined }));
  const claims = v.claims?.filter(Boolean).length ? v.claims.filter(Boolean) : product.claims;
  const marquee = v.marquee?.filter(Boolean).length ? v.marquee.filter(Boolean) : claims;
  const howTo = v.howTo?.filter(Boolean).length ? v.howTo.filter(Boolean) : product.howToUse;
  const inci = v.inci || product.inci;
  const faqs = v.faqs?.filter((f) => f.question).length ? v.faqs.filter((f) => f.question) : product.faqs;
  const [photo, setPhoto] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [skin, setSkin] = useState(0);
  const [sbar, setSbar] = useState(false);
  const perks = useRef<HTMLDivElement>(null);
  const dlg = useRef<HTMLDialogElement>(null);
  const dlgVideo = useRef<HTMLVideoElement>(null);
  const stepVideo = useRef<HTMLVideoElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const baFrame = useRef<HTMLDivElement>(null);
  const [ba, setBa] = useState(50);
  const [rSkin, setRSkin] = useState('all');
  const [rStar, setRStar] = useState(0);
  const [rSort, setRSort] = useState('new');
  const [rShown, setRShown] = useState(4);
  const rdlg = useRef<HTMLDialogElement>(null);
  const [rRate, setRRate] = useState(0);
  const [rHover, setRHover] = useState(0);
  const [rSent, setRSent] = useState(false);
  const [rBusy, setRBusy] = useState(false);
  const [helped, setHelped] = useState<Record<number, boolean>>({});
  const [skinFade, setSkinFade] = useState(false);
  const [ritStep, setRitStep] = useState(0);
  const [ritOn, setRitOn] = useState(false);
  const ritHover = useRef(false);
  const ritList = useRef<HTMLOListElement>(null);
  const ingRow = useRef<HTMLDivElement>(null);
  const [ingDot, setIngDot] = useState(0);
  const [toast, setToast] = useState('');
  const toastTimer = useRef(0);
  const wishlist = host.wishlist;
  const wished = !!wishlist?.has(product.id);

  const say = (m: string) => {
    setToast(m);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(''), 1800);
  };

  // Reveal on scroll. Runs after every render so sections that appear later
  // (content loading, product switch) are picked up too.
  const io = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    io.current = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.current?.unobserve(e.target);
          }
        }),
      { threshold: 0.12 },
    );
    const el = root.current;
    return () => {
      io.current?.disconnect();
      el?.querySelectorAll('[data-io]').forEach((n) => n.removeAttribute('data-io'));
    };
  }, []);
  useEffect(() => {
    root.current?.querySelectorAll('.rv:not(.in):not([data-io]),#ritual:not([data-io])').forEach((n) => {
      n.setAttribute('data-io', '');
      io.current?.observe(n);
    });
  });

  // Sticky buy bar once the main buttons have scrolled away.
  useEffect(() => {
    const on = () => setSbar(!!perks.current && perks.current.getBoundingClientRect().bottom < 0);
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);

  // The step clip plays only while on screen.
  useEffect(() => {
    const vid = stepVideo.current;
    if (!vid || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(([e]) => (e.isIntersecting ? vid.play().catch(() => {}) : vid.pause()), { threshold: 0.35 });
    io.observe(vid);
    return () => io.disconnect();
  }, [v.watch?.clip]);

  useEffect(() => {
    setPhoto(0);
    setSkin(0);
  }, [product.id]);

  // A gallery video plays only while it is the photo on show.
  useEffect(() => {
    stage.current?.querySelectorAll('video').forEach((vid) => (Number(vid.dataset.i) === photo ? vid.play().catch(() => {}) : vid.pause()));
  }, [photo]);

  // Accordions open and close smoothly.
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const onClick = (e: MouseEvent) => {
      const sum = (e.target as HTMLElement).closest('.acc summary');
      if (!sum) return;
      const d = sum.parentElement as HTMLDetailsElement;
      const b = d.querySelector<HTMLElement>('.body');
      if (!b || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      e.preventDefault();
      const ease = 'cubic-bezier(.16,1,.3,1)';
      if (d.open) b.animate([{ height: b.scrollHeight + 'px' }, { height: '0px' }], { duration: 350, easing: ease }).onfinish = () => (d.open = false);
      else {
        d.open = true;
        b.animate([{ height: '0px' }, { height: b.scrollHeight + 'px' }], { duration: 450, easing: ease });
      }
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, []);

  // Ritual: light up one step at a time while it's on screen; hover picks a step.
  const ritCount = v.ritual?.steps?.length ?? 0;
  useEffect(() => {
    const list = ritList.current;
    if (!list || !ritCount || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let timer = 0;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !timer) {
          setRitOn(true);
          timer = window.setInterval(() => !ritHover.current && setRitStep((k) => (k + 1) % ritCount), 2400);
        } else if (!e.isIntersecting) {
          window.clearInterval(timer);
          timer = 0;
        }
      },
      { threshold: 0.35 },
    );
    io.observe(list);
    return () => {
      io.disconnect();
      window.clearInterval(timer);
    };
  }, [ritCount]);
  useEffect(() => {
    const list = ritList.current;
    if (!list || !ritOn) return;
    const place = () => {
      const li = list.querySelectorAll('li')[ritStep];
      const rail = list.querySelector<HTMLElement>('.rail');
      const ico = li?.querySelector<HTMLElement>('.ico');
      if (!li || !rail || !ico) return;
      const y = li.offsetTop + ico.offsetTop + ico.offsetHeight / 2 - rail.offsetTop;
      (rail.querySelector('b') as HTMLElement).style.top = y - 4.5 + 'px';
      (rail.querySelector('i') as HTMLElement).style.transform = `scaleY(${Math.max(0, Math.min(1, y / rail.offsetHeight))})`;
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [ritStep, ritOn]);

  // Phones: ingredient cards slide on their own every 3.5s; a touch pauses it.
  useEffect(() => {
    const row = ingRow.current;
    if (!row) return;
    let hold = false;
    let t = 0;
    const mark = () => {
      const first = row.children[0] as HTMLElement | undefined;
      const i = first ? Math.round(row.scrollLeft / (first.offsetWidth + 10)) : 0;
      setIngDot(i);
      return i;
    };
    const touch = () => {
      hold = true;
      window.clearTimeout(t);
      t = window.setTimeout(() => (hold = false), 6000);
    };
    row.addEventListener('scroll', mark, { passive: true });
    row.addEventListener('touchstart', touch, { passive: true });
    const iv = window.setInterval(() => {
      if (hold || !window.matchMedia('(max-width:600px)').matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const r = row.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      const cards = [...row.children] as HTMLElement[];
      const n = (mark() + 1) % cards.length;
      row.scrollTo({ left: cards[n].offsetLeft - cards[0].offsetLeft, behavior: 'smooth' });
    }, 3500);
    return () => {
      window.clearInterval(iv);
      window.clearTimeout(t);
      row.removeEventListener('scroll', mark);
      row.removeEventListener('touchstart', touch);
    };
  }, [product.id]);

  // Before / after: one gentle sweep of the handle the first time it shows.
  useEffect(() => {
    const f = baFrame.current;
    if (!f || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        const t0 = performance.now() + 600;
        const step = (now: number) => {
          const p = Math.max(0, Math.min(1, (now - t0) / 2400));
          setBa(50 + Math.sin(p * Math.PI * 2) * 24 * (1 - p));
          if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
      },
      { threshold: 0.5 },
    );
    io.observe(f);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [v.beforeAfter?.before, v.beforeAfter?.after]);

  const add = () => {
    if (outOfStock) return;
    onAddToCart(qty);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1800);
  };
  const buyNow = () => {
    if (outOfStock) return;
    onAddToCart(qty);
    host.checkout();
  };
  // The video dialog: a given video (e.g. a YouTube clip) from `start`, or
  // the full routine (watch.full, else the built-in film) at `start`.
  const [dlgSrc, setDlgSrc] = useState('');
  const [dlgStart, setDlgStart] = useState(0);
  const [dlgOpen, setDlgOpen] = useState(false);
  const openVideo = (start: number, src?: string) => {
    const url = src || v.watch?.full || '';
    setDlgSrc(url);
    setDlgStart(start);
    setDlgOpen(true);
    dlg.current?.showModal();
    if (ytId(url)) return;
    const vid = dlgVideo.current;
    if (!vid) return;
    const go = () => {
      vid.currentTime = start;
      vid.play().catch(() => {});
    };
    if (vid.readyState > 0) go();
    else {
      vid.addEventListener('loadedmetadata', go, { once: true });
      vid.load();
    }
  };
  const share = () => {
    if (navigator.share) navigator.share({ title: document.title, url: location.href }).catch(() => {});
    else navigator.clipboard?.writeText(location.href).then(() => say('Link copied'));
  };
  const [wishPop, setWishPop] = useState(0);
  const toggleWish = () => {
    setWishPop((n) => n + 1);
    if (!wishlist) return;
    say(wishlist.toggle(product.id) ? 'Saved to your wishlist' : 'Removed from your wishlist');
  };
  const pickSkin = (i: number) => {
    if (i === skin) return;
    setSkinFade(true);
    window.setTimeout(() => {
      setSkin(i);
      setSkinFade(false);
    }, 250);
  };
  const openReview = () => {
    if (!currentUser) {
      navigate(links.login);
      return;
    }
    setRRate(0);
    setRSent(false);
    rdlg.current?.showModal();
  };
  const sendReview = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const text = String(f.get('text') ?? '').trim();
    if (!currentUser || !rRate || !text || !host.submitReview) return;
    setRBusy(true);
    try {
      await host.submitReview({
        sku: product.sku,
        customerId: currentUser.id,
        customerName: String(f.get('name') ?? '').trim() || currentUser.name,
        skinType: String(f.get('skin') ?? ''),
        rating: rRate,
        reviewText: text,
      });
      setRSent(true);
    } catch {
      showToast(t('products.reviewSubmitError', 'Could not submit your review. Please try again.'));
    } finally {
      setRBusy(false);
    }
  };
  const closeVideo = () => {
    setDlgOpen(false);
    dlgVideo.current?.pause();
    dlg.current?.close();
  };

  const stepIndex = STEPS.indexOf(product.stepKeyword);
  const nameParts = name.split(' ');
  const routine = products.filter((p) => STEPS.includes(p.stepKeyword)).sort((a, b) => a.step - b.step);
  const skins = v.skins?.length
    ? v.skins.filter((s) => s.label && s.tip).map((s) => ({ label: s.label, name: s.label.replace(/\n/g, ' '), image: s.image, tip: s.tip }))
    : product.skinGuide
        .filter((s) => s.type && s.tip)
        .map((s) => ({
          label: s.type.replace(/ skin$/i, ''),
          name: s.type,
          image: SKIN_PHOTO[s.type.toLowerCase().replace(/ skin$/, '')] || SKIN_PHOTO.normal,
          tip: s.tip,
        }));
  const ings = v.ingredients?.items?.length
    ? v.ingredients.items
    : product.ingredientDetails
        .slice(0, 4)
        .map((g) => ({ tag: g.concentration ?? '', name: g.name.replace(/\s*\d[\d.\-–]*%$/, ''), text: g.benefitSummary, inci: g.inciFull ?? '' }));
  // Staff previewing the new design see the draft's sample reviews until real ones exist.
  const samples = isPreview && !reviews.length ? (v.sampleReviews ?? []) : [];
  const revs = samples.length
    ? samples.map((r) => ({ name: r.name, skin: r.skin, rating: r.rating, date: r.date, text: r.text, verified: true, title: r.title, helpful: r.helpful as number | undefined }))
    : reviews.map((r) => ({ ...r, title: '', helpful: undefined as number | undefined }));
  const avg = revs.length ? revs.reduce((s, r) => s + r.rating, 0) / revs.length : 0;
  const shownReviews = revs
    .filter((r) => (rSkin === 'all' || r.skin.toLowerCase() === rSkin.toLowerCase()) && (!rStar || r.rating === rStar))
    .sort((a, b) => (rSort === 'new' ? b.date.localeCompare(a.date) : rSort === 'high' ? b.rating - a.rating : a.rating - b.rating));
  const ba2 = v.beforeAfter;

  return (
    <div className="v2p" ref={root}>
      <div className="wrap" style={{ paddingTop: 'var(--v2h-h, 64px)' }}>
        <div className="crumbs">
          <Link to={links.home}>{links.homeLabel || 'Home'}</Link>
          <span>/</span>
          <Link to={links.products}>{t('nav.products', 'Products')}</Link>
          <span>/</span>
          {name}
        </div>

        {/* Product: gallery + purchase */}
        <section className="pdp">
          <div className="gal">
            <div className="thumbs" role="group" aria-label="Product photos">
              {photos.map((m, i) => (
                <button key={m.src + i} type="button" aria-pressed={i === photo} aria-label={isVideo(m.src) ? 'Play video' : `Photo ${i + 1}`} onClick={() => setPhoto(i)}>
                  <img src={isVideo(m.src) ? m.poster || ytThumb(m.src) : m.src} alt="" />
                  {isVideo(m.src) && (
                    <span className="play">
                      <Svg d="c:cx=12,cy=12,r=11|M10 8l6 4-6 4z" />
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div>
              <div
                className="stage"
                ref={stage}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  setPhoto(Math.round(el.scrollLeft / el.clientWidth));
                }}
              >
                {STEP_TAG[product.stepKeyword] && <span className="tag">{STEP_TAG[product.stepKeyword]}</span>}
                {photos.map((m, i) =>
                  ytId(m.src) ? (
                    <YtBg key={m.src + i} url={m.src} poster={m.poster} className={`stv ${i === photo ? 'cur' : 'out'}`} />
                  ) : isVideo(m.src) ? (
                    <video
                      key={m.src + i}
                      data-i={i}
                      src={m.src}
                      poster={m.poster}
                      muted
                      loop
                      playsInline
                      preload="none"
                      aria-label="Texture video"
                      className={i === photo ? 'cur' : 'out'}
                    />
                  ) : (
                    <img key={m.src + i} src={m.src} alt={i === 0 ? product.imageAlt : ''} className={i === photo ? 'cur' : 'out'} />
                  ),
                )}
              </div>
              <div className="dots">
                {photos.map((_, i) => (
                  <i key={i} className={i === photo ? 'on' : ''} />
                ))}
              </div>
            </div>
          </div>

          <div className="buy">
            {(stepIndex >= 0 || isSet) && (
              <div className="step rise" style={{ ['--d' as string]: '.05s' }}>
                {STEPS.map((s, i) => (
                  <i key={s} className={isSet || i === stepIndex ? 'on' : ''}>
                    0{i + 1}
                  </i>
                ))}
                <span>3-STEP DAILY TREATMENT</span>
              </div>
            )}
            <h1 className="rise" style={{ ['--d' as string]: '.12s' }}>
              {v.title || nameParts.slice(0, Math.ceil(nameParts.length / 2)).join(' ')}
              <br />
              <em>{v.title ? v.titleEm : nameParts.slice(Math.ceil(nameParts.length / 2)).join(' ')}</em>
            </h1>
            <div className="slogan rise" style={{ ['--d' as string]: '.18s' }}>
              {v.slogan || product.subtitle}
            </div>
            {revs.length > 0 && (
              <a className="rating rise" href="#reviews" style={{ ['--d' as string]: '.2s' }}>
                <span className="stars" style={{ ['--p' as string]: `${(avg / 5) * 100}%` }}>
                  ★★★★★
                </span>
                <b>{avg.toFixed(1)}</b>
                <span>{revs.length} reviews</span>
              </a>
            )}
            <div className="price rise" style={{ ['--d' as string]: '.22s' }}>
              <b>{product.price}</b>
              {isSet && routineSet?.originalPrice && routineSet.savings ? (
                <>
                  <s>{routineSet.originalPrice}</s>
                  <span className="save">SAVE {routineSet.savings}</span>
                </>
              ) : (
                <span>{product.volume.replace('ml', ' ml')}</span>
              )}
              <span className="pact">
                {wishlist && (
                  <button key={wishPop} type="button" className={`ib${wishPop ? ' pop' : ''}`} aria-label="Save to wishlist" aria-pressed={wished} onClick={toggleWish}>
                    <Svg d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" />
                  </button>
                )}
                <button type="button" className="ib" aria-label="Share" onClick={share}>
                  <Svg d={ICON.share} />
                </button>
              </span>
            </div>
            <p className="lede rise" style={{ ['--d' as string]: '.26s' }}>
              {v.lede || product.description}
            </p>
            {claims.length > 0 && (
              <div className="claims rise" style={{ ['--d' as string]: '.3s' }}>
                {claims.slice(0, 5).map((c) => (
                  <span key={c}>{c}</span>
                ))}
              </div>
            )}
            <div className="ctas rise" style={{ ['--d' as string]: '.34s' }}>
              <button type="button" className="btn wine buynow" onClick={buyNow} disabled={outOfStock}>
                <span>{outOfStock ? t('products.outOfStock', 'Out of stock') : t('common.buyNow', 'Buy now')}</span>
                <small>Cash on delivery</small>
              </button>
              <div className="row">
                <div className="qty">
                  <button type="button" aria-label="Less" onClick={() => setQty((q) => Math.max(1, q - 1))}>
                    −
                  </button>
                  <output>{qty}</output>
                  <button type="button" aria-label="More" onClick={() => setQty((q) => Math.min(10, q + 1))}>
                    +
                  </button>
                </div>
                <button type="button" className={`btn ghost${added ? ' done' : ''}`} onClick={add} disabled={outOfStock}>
                  {added ? (
                    `✓ ${t('cart.added', 'Added to cart')}`
                  ) : (
                    <>
                      <span className="ic">
                        <Svg d={ICON.bag} />
                      </span>
                      <span>{t('common.addToCart', 'Add to cart')}</span>
                    </>
                  )}
                </button>
              </div>
              <p className="ctanote">
                <Svg d={ICON.shield} />
                Pay the rider when it arrives · no advance needed
              </p>
            </div>
            {isSet && <SetBox routine={routine} routineSet={routineSet} href={links.product} />}
            {routineSet && !isSet && links.set && (
              <Link className="set rise" to={links.set} style={{ ['--d' as string]: '.42s' }}>
                <img src={routineSet.image} alt="" />
                <span>
                  <b>Complete the ritual</b>
                  <small>Booster + Serum + Cream in a gift box</small>
                </span>
                <span style={{ textAlign: 'right' }}>
                  <b>{routineSet.price}</b>
                  {routineSet.savings && <em>SAVE {routineSet.savings}</em>}
                </span>
              </Link>
            )}
            <div className="perks rise" ref={perks} style={{ ['--d' as string]: '.46s' }}>
              <div>
                <Svg d="M3 6h18v12H3z|c:cx=12,cy=12,r=2.5" />
                Cash on delivery across Bangladesh
              </div>
              <div>
                <Svg d="M3 7h11v9H3zM14 10h4l3 3v3h-7|c:cx=7,cy=17,r=1.6|c:cx=17,cy=17,r=1.6" />
                {isSet ? 'Free delivery on this set, anywhere in Bangladesh' : 'Delivery ৳70 in Dhaka, ৳120 outside · free over ৳3,000'}
              </div>
              <div>
                <Svg d={ICON.shield} />
                Genuine Rx.K Seoul · check your box on our Verify page
              </div>
            </div>
            <div className="acc rise" style={{ ['--d' as string]: '.5s' }}>
              {(v.benefits || product.description) && (
                <details open>
                  <summary>
                    Benefits <i />
                  </summary>
                  <div className="body">
                    <div>{v.benefits || product.description}</div>
                  </div>
                </details>
              )}
              {howTo.length > 0 && (
                <details>
                  <summary>
                    How to use <i />
                  </summary>
                  <div className="body">
                    <div>
                      <ol>
                        {howTo.map((s) => (
                          <li key={s}>{s}</li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </details>
              )}
              {inci && (
                <details>
                  <summary>
                    Full ingredients <i />
                  </summary>
                  <div className="body">
                    <div>{inci}</div>
                  </div>
                </details>
              )}
            </div>
          </div>
        </section>
      </div>

      {on('marquee') && marquee.length > 0 && (
        <div className="marquee" aria-hidden="true">
          <div className="track">
            {[...marquee, ...marquee].map((c, i) => (
              <span key={i}>{c}</span>
            ))}
          </div>
        </div>
      )}

      {/* Why use it */}
      {on('why') && !!v.concerns?.length && (
        <section className="sec why">
          <div className="wrap">
            <div className="head center rv">
              <span className="kicker">Why use it</span>
              <h2>
                Why use the
                <br />
                <em>{v.whyName || name}?</em>
              </h2>
              {v.whyLead && <p>{v.whyLead}</p>}
            </div>
            <div className="rows">
              {v.concerns.map((c, i) => (
                <article key={i} className="wrow rv">
                  <figure className="wimg">
                    <img src={c.image} alt="" loading="lazy" />
                    <span className="q">Concern 0{i + 1}</span>
                  </figure>
                  <div className="wtxt">
                    <h3>{c.question}</h3>
                    <div className="wl">
                      <div>
                        <span className="ic">
                          <Svg d={ICON.q} />
                        </span>
                        <div>
                          <b>Why it happens</b>
                          <p>{c.why}</p>
                        </div>
                      </div>
                      <div>
                        <span className="ic">
                          <Svg d={ICON.h} />
                        </span>
                        <div>
                          <b>{v.helps || 'How it helps'}</b>
                          <p>{c.how}</p>
                        </div>
                      </div>
                      <div className="res">
                        <span className="ic">
                          <Svg d={ICON.ok} />
                        </span>
                        <div>
                          <b>What you'll notice</b>
                          <p>{c.result}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            {on('beforeAfter') && ba2?.before && ba2.after && (
              <div className="ba">
                <div className="head rv" style={{ margin: 0 }}>
                  <span className="kicker">Real results</span>
                  <h2>
                    Before
                    <br />
                    <em>and after.</em>
                  </h2>
                  <p>Drag the handle to compare. Every photo is from a real customer, shared with permission.</p>
                  <p className="ba-note">
                    {ba2.sample ? 'Design preview with a sample image. Real customer photos replace it before launch. Individual results vary.' : 'Individual results vary.'}
                  </p>
                </div>
                <div className="ba-frame rv" ref={baFrame} style={{ ['--d' as string]: '.12s', ['--x' as string]: `${ba}%` }}>
                  <div className="ba-after">
                    <img src={ba2.after} alt="" />
                    <span className="tag r">{ba2.afterLabel || 'After'}</span>
                  </div>
                  <div className="ba-before">
                    <img src={ba2.before} alt="" />
                    <span className="tag">Before</span>
                  </div>
                  {ba2.sample && <span className="sample-tag">Sample image · not a real result</span>}
                  <span className="ba-line" aria-hidden="true">
                    <i>
                      <Svg d="M9 7l-5 5 5 5M15 7l5 5-5 5" />
                    </i>
                  </span>
                  <input type="range" min="0" max="100" step="0.5" value={ba} aria-label="Compare before and after" onChange={(e) => setBa(Number(e.target.value))} />
                </div>
              </div>
            )}
            <div className="wcta rv">
              <div>
                <h3>Sounds like your skin?</h3>
                <p>{v.cta || 'Add it to your routine, morning and night.'}</p>
              </div>
              <b className="p">{product.price}</b>
              <button type="button" className="btn wine" onClick={add} disabled={outOfStock}>
                <span className="ic">
                  <Svg d={ICON.bag} />
                </span>
                {t('common.addToCart', 'Add to cart')}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Skin types */}
      {on('skins') && skins.length > 0 && (
        <section className="sec soft skinsec">
          <div className="wrap">
            <div className="skinrow">
              <div className="skinhead rv">
                <span className="kicker">For every skin</span>
                <h2>
                  Suitable for <br />
                  <em>all skin types.</em>
                </h2>
                <p>Tap yours to see how to use it.</p>
              </div>
              <div className="skins" role="tablist">
                {skins.map((s, i) => (
                  <button key={s.name} type="button" role="tab" aria-selected={i === skin} className="rv" style={{ ['--d' as string]: `${i * 0.08}s` }} onClick={() => pickSkin(i)}>
                    <span className="av">
                      <img src={s.image} alt="" />
                    </span>
                    <b>{lines(s.label)}</b>
                  </button>
                ))}
              </div>
            </div>
            <div className="skincard rv">
              <span className={`kk${skinFade ? ' fade' : ''}`}>{skins[skin]?.name}</span>
              <p className={`tpanel${skinFade ? ' fade' : ''}`} aria-live="polite">
                {skins[skin]?.tip}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Key ingredients */}
      {on('ingredients') && ings.length > 0 && (
        <section className="sec soft">
          <div className="wrap">
            <div className="head rv">
              <span className="kicker">Key ingredients</span>
              <h2>
                {v.ingredients?.title || 'What’s inside,'}
                <br />
                <em>{v.ingredients?.titleEm || 'and why.'}</em>
              </h2>
            </div>
            <div className="ings" ref={ingRow}>
              {ings.map((g, i) => (
                <article key={g.name} className="ing rv" style={{ ['--d' as string]: `${i * 0.08}s` }}>
                  <span className="n">0{i + 1}</span>
                  {g.tag && <span className="c">{g.tag}</span>}
                  <h3>{g.name}</h3>
                  <p>{g.text}</p>
                  {g.inci && <span className="inci">{g.inci}</span>}
                </article>
              ))}
            </div>
            <div className="ingdots">
              {ings.map((g, k) => (
                <i key={g.name} className={k === ingDot ? 'on' : ''} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ritual (the set shows the scroll story instead) */}
      {on('ritual') && isSet && routine.length > 0 && <SetStory routine={routine} v={v} href={links.product} />}
      {on('ritual') && !isSet && !!v.ritual?.steps?.length && (
        <section className="sec ritsec">
          <div className="wrap rit">
            <div className="rit-l">
              <div className="head rv" style={{ margin: 0 }}>
                <span className="kicker">{v.ritual.kicker}</span>
                <h2>
                  {v.ritual.title}
                  <br />
                  <em>{v.ritual.titleEm}</em>
                </h2>
              </div>
              {(v.ritual.am || v.ritual.pm) && (
                <div className="ampm rv" style={{ ['--d' as string]: '.1s' }}>
                  <div>
                    <Svg d="c:cx=12,cy=12,r=4|M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
                    <span>
                      <b>Morning</b>
                      {v.ritual.am}
                    </span>
                  </div>
                  <div>
                    <Svg d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />
                    <span>
                      <b>Night</b>
                      {v.ritual.pm}
                    </span>
                  </div>
                </div>
              )}
              {v.ritual.image && (
                <figure className="rit-img rv" style={{ ['--d' as string]: '.18s' }}>
                  <img src={v.ritual.image} alt={name} loading="lazy" />
                  <span className="shine" aria-hidden="true" />
                  <span className="bubbles" aria-hidden="true">
                    {Array.from({ length: 7 }, (_, i) => (
                      <i key={i} />
                    ))}
                  </span>
                  {v.ritual.glassTitle && (
                    <span className="glass">
                      <b>{v.ritual.glassTitle}</b>
                      {v.ritual.glassText}
                    </span>
                  )}
                </figure>
              )}
            </div>
            <ol className={`steps${ritOn ? ' playing' : ''}`} id="ritual" ref={ritList}>
              <span className="rail" aria-hidden="true">
                <i />
                <b />
              </span>
              {v.ritual.steps.map((s, i) => (
                <li
                  key={i}
                  className={`rv${ritOn && i === ritStep ? ' on' : ''}`}
                  style={{ ['--d' as string]: `${i * 0.1}s` }}
                  onMouseEnter={() => {
                    ritHover.current = true;
                    setRitOn(true);
                    setRitStep(i);
                  }}
                  onMouseLeave={() => (ritHover.current = false)}
                >
                  <span className="ico">
                    <Svg d={STEP_ICONS[i % STEP_ICONS.length]} box="0 0 32 32" />
                  </span>
                  <div>
                    <span className="t">{s.time}</span>
                    <h3>{s.title}</h3>
                    <p>{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* Watch how to use */}
      {on('watch') && v.watch?.clip && (
        <section className="sec hw">
          <div className="wrap hwg">
            <div className="hwv rv">
              {ytId(v.watch.clip) ? (
                <YtBg url={v.watch.clip} poster={v.watch.poster} className="hwbg" />
              ) : (
                <video ref={stepVideo} src={v.watch.clip} poster={v.watch.poster} muted loop playsInline preload="none" />
              )}
              {v.watch.label && <span className="hwt">{v.watch.label}</span>}
              <button
                type="button"
                className="hwp"
                aria-label="Watch with sound"
                onClick={() => (ytId(v.watch?.clip) ? openVideo(0, v.watch?.clip) : openVideo(v.watch?.start ?? 0))}
              >
                <Svg d={ICON.play} />
              </button>
            </div>
            <div className="hwx rv" style={{ ['--d' as string]: '.1s' }}>
              <div className="head" style={{ margin: 0 }}>
                <span className="kicker">Watch how to use</span>
                <h2>
                  See it on
                  <br />
                  <em>real skin.</em>
                </h2>
                {v.watch.line && <p className="hwl">{v.watch.line}</p>}
              </div>
              <div className="hws">
                {routine.map((p) => {
                  const w = p.designV2?.watch;
                  if (!w?.clip) return null;
                  return (
                    <button key={p.id} type="button" className={p.id === product.id ? 'on' : ''} onClick={() => (ytId(w.clip) ? openVideo(0, w.clip) : openVideo(w.start ?? 0))}>
                      <img src={w.poster || ytThumb(w.clip)} alt="" />
                      <span>
                        <small>{STEP_TAG[p.stepKeyword]?.split(' · ')[0]}</small>
                        {p.designV2?.short || p.name}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button type="button" className="btn wine hwfull" onClick={() => openVideo(0)}>
                <Svg d={ICON.play} />
                <span>{v.watch.full ? 'Watch the full routine' : 'Watch the full routine · 0:40'}</span>
              </button>
            </div>
          </div>
        </section>
      )}
      <dialog
        className="hwd"
        ref={dlg}
        aria-label="Video"
        onClick={(e) => e.target === dlg.current && closeVideo()}
        onClose={() => {
          setDlgOpen(false);
          dlgVideo.current?.pause();
        }}
      >
        {ytId(dlgSrc) ? (
          dlgOpen && <YtPlayer url={dlgSrc} start={dlgStart} className="hwyt" />
        ) : (
          <video ref={dlgVideo} src={dlgSrc || '/v2/vid-routine.mp4'} poster={dlgSrc ? undefined : '/v2/vid-routine.jpg'} controls playsInline preload="none" />
        )}
        <button type="button" className="hwc" aria-label="Close video" onClick={closeVideo}>
          ×
        </button>
      </dialog>

      {/* Texture (the set shows morning/night, results and the gift box instead) */}
      {on('texture') && isSet && routine.length > 0 && <SetDayNight routine={routine} v={v} />}
      {on('texture') && isSet && <SetGift v={v} price={product.price} onAdd={add} />}
      {on('texture') && !isSet && v.texture?.title && (
        <section className="sec">
          <div className="wrap tex">
            {ytId(v.texture.media) ? (
              <YtBg url={v.texture.media as string} poster={v.texture.poster} className="rv txbg" />
            ) : isVideo(v.texture.media) ? (
              <video className="rv" src={v.texture.media} poster={v.texture.poster} muted loop playsInline autoPlay />
            ) : (
              <img className="rv ph" src={v.texture.media || product.image} alt={name} loading="lazy" />
            )}
            <div className="head rv" style={{ ['--d' as string]: '.12s', margin: 0 }}>
              <span className="kicker">Texture</span>
              <h2>
                {v.texture.title}
                <br />
                <em>{v.texture.titleEm}</em>
              </h2>
              <p>{v.texture.text}</p>
              <div className="meter">
                <div className="bar">
                  {[8, 50, 92].map((x, i) => (
                    <span key={x} className={`pin${i === (v.texture?.level ?? 0) ? ' on' : ''}`} style={{ left: `${x}%` }} />
                  ))}
                </div>
                <div className="lb">
                  {['Booster · light', 'Serum · silky', 'Cream · rich'].map((l, i) => (i === (v.texture?.level ?? 0) ? <b key={l}>{l}</b> : <span key={l}>{l}</span>))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* The 3-step treatment */}
      {on('routine') && routine.length > 1 && (
        <section className="sec">
          <div className="wrap">
            <div className="head center rv">
              <span className="kicker">The 3-step treatment</span>
              <h2>
                {v.routineTitle || 'Better together,'}
                <br />
                <em>{v.routineTitleEm || 'step by step.'}</em>
              </h2>
            </div>
            {isSet && routineSet && <SetSavings routine={routine} routineSet={routineSet} onBuy={buyNow} />}
            <div className="routine">
              {routine.map((p, i) => (
                <Link key={p.id} className={`pc rv${p.id === product.id ? ' here' : ''}`} style={{ ['--d' as string]: `${i * 0.1}s` }} to={links.product(p) ?? '#'}>
                  <div className="im">
                    <span className="badge">{STEP_TAG[p.stepKeyword]}</span>
                    <img src={p.image} alt="" loading="lazy" />
                  </div>
                  <small>{p.subtitle}</small>
                  <h3>{p.designV2?.name || p.name}</h3>
                  <span className="p">{p.price}</span>
                </Link>
              ))}
            </div>
            {routineSet && !isSet && links.set && (
              <div className="offer rv">
                <img src={routineSet.image} alt="" />
                <div>
                  <h3>{routineSet.name}</h3>
                  <p>All three steps in one gift box. The complete daily ritual, ready to give.</p>
                </div>
                <Link className="btn" to={links.set}>
                  {routineSet.price}
                  {routineSet.savings ? ` · Save ${routineSet.savings}` : ''}
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Reviews: approved rxk_product_reviews rows only */}
      {on('reviews') && (
        <section className="sec soft" id="reviews">
          <div className="wrap">
            <div className="rvhead">
              <div className="head rv" style={{ margin: 0 }}>
                <span className="kicker">Reviews</span>
                <h2>
                  What customers
                  <br />
                  <em>are saying.</em>
                </h2>
              </div>
              {samples.length > 0 && (
                <p className="sample rv" style={{ ['--d' as string]: '.1s' }}>
                  Design preview with sample reviews (staff only). The live page shows only approved reviews from Rx.K Seoul customers.
                </p>
              )}
            </div>
            <div className="revs">
              <aside className="rsum rv">
                <div className="big">
                  <b>{revs.length ? avg.toFixed(1) : '–'}</b>
                  <span className="stars" style={{ ['--p' as string]: pct(avg) }}>
                    ★★★★★
                  </span>
                  <small>{revs.length ? `Based on ${revs.length} review${revs.length > 1 ? 's' : ''}` : 'No reviews yet'}</small>
                </div>
                <div className="dist">
                  {[5, 4, 3, 2, 1].map((n) => {
                    const c = revs.filter((r) => r.rating === n).length;
                    return (
                      <button
                        key={n}
                        type="button"
                        aria-pressed={rStar === n}
                        onClick={() => {
                          setRStar(rStar === n ? 0 : n);
                          setRShown(4);
                        }}
                      >
                        {n} ★
                        <span className="bar">
                          <i style={{ ['--w' as string]: `${revs.length ? Math.round((c / revs.length) * 100) : 0}%` }} />
                        </span>
                        {c}
                      </button>
                    );
                  })}
                </div>
                {host.submitReview && (
                  <>
                    <button type="button" className="btn dark" onClick={openReview}>
                      Write a review
                    </button>
                    <p className="rnote">Reviews appear after our team checks them.</p>
                  </>
                )}
              </aside>
              <div className="rv" style={{ ['--d' as string]: '.1s' }}>
                <div className="rbar">
                  <div className="chips">
                    {['all', ...REVIEW_SKINS].map((k) => (
                      <button
                        key={k}
                        type="button"
                        aria-pressed={rSkin === k}
                        onClick={() => {
                          setRSkin(k);
                          setRShown(4);
                        }}
                      >
                        {k === 'all' ? 'All' : k}
                      </button>
                    ))}
                  </div>
                  <select aria-label="Sort reviews" value={rSort} onChange={(e) => setRSort(e.target.value)}>
                    <option value="new">Newest</option>
                    <option value="high">Highest rated</option>
                    <option value="low">Lowest rated</option>
                  </select>
                </div>
                {shownReviews.length ? (
                  shownReviews.slice(0, rShown).map((r, i) => (
                    <article key={r.name + r.date + i} className="rc">
                      <div className="top">
                        <div className="who">
                          <span className="av">{r.name[0]}</span>
                          <span>
                            <b>{r.name}</b>
                            <small>
                              {r.skin ? `${r.skin} skin · ` : ''}
                              {new Date(r.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </small>
                          </span>
                        </div>
                        <span className="stars" style={{ ['--p' as string]: pct(r.rating) }} aria-label={`${r.rating} out of 5`}>
                          ★★★★★
                        </span>
                      </div>
                      {r.title && <h4>{r.title}</h4>}
                      <p>{r.text}</p>
                      {(r.verified || r.helpful !== undefined) && (
                        <div className="foot">
                          {r.verified && (
                            <span className="ver">
                              <Svg d={ICON.check} />
                              Verified purchase
                            </span>
                          )}
                          {r.helpful !== undefined && (
                            <button type="button" className="help" aria-pressed={!!helped[i]} onClick={() => setHelped((h) => ({ ...h, [i]: !h[i] }))}>
                              Helpful · <span>{r.helpful + (helped[i] ? 1 : 0)}</span>
                            </button>
                          )}
                        </div>
                      )}
                    </article>
                  ))
                ) : (
                  <p className="rempty">{revs.length ? 'No reviews match this filter yet.' : 'Be the first to review this product.'}</p>
                )}
                {shownReviews.length > rShown && (
                  <button type="button" className="btn ghost more" onClick={() => setRShown((n) => n + 4)}>
                    Show more reviews
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
      <dialog className={`rdlg${rSent ? ' sent' : ''}`} ref={rdlg} onClick={(e) => e.target === rdlg.current && rdlg.current?.close()}>
        <button type="button" className="x" aria-label="Close" onClick={() => rdlg.current?.close()}>
          ×
        </button>
        <form onSubmit={sendReview}>
          <h3>Write a review</h3>
          <p className="sub">{name}</p>
          <label>
            Your rating
            <span className="pick" onMouseLeave={() => setRHover(0)}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  aria-label={`${n} star${n > 1 ? 's' : ''}`}
                  className={n <= (rHover || rRate) ? 'on' : ''}
                  onMouseOver={() => setRHover(n)}
                  onClick={() => setRRate(n)}
                >
                  ★
                </button>
              ))}
            </span>
          </label>
          <div className="two">
            <label>
              Name
              <input name="name" required maxLength={40} defaultValue={currentUser?.name ?? ''} />
            </label>
            <label>
              Skin type
              <select name="skin">
                {REVIEW_SKINS.map((k) => (
                  <option key={k}>{k}</option>
                ))}
              </select>
            </label>
          </div>
          <label>
            Your review
            <textarea name="text" required maxLength={600} placeholder="How does it feel on your skin?" />
          </label>
          <button type="submit" className="btn wine" disabled={rBusy || !rRate}>
            Submit review
          </button>
        </form>
        <div className="ok">
          <Svg d="c:cx=12,cy=12,r=10|M7.5 12.5l3 3 6-7" />
          <h3>Thank you</h3>
          <p>Your review will appear after our team approves it.</p>
          <button type="button" className="btn ghost more" onClick={() => rdlg.current?.close()}>
            Close
          </button>
        </div>
      </dialog>

      {/* FAQ */}
      {on('faq') && faqs.length > 0 && (
        <section className="sec" id="faq">
          <div className="wrap faq">
            <div className="head rv" style={{ margin: 0 }}>
              <span className="kicker">Questions</span>
              <h2>
                Good to
                <br />
                <em>know.</em>
              </h2>
              <p>{v.faqLine || 'Still unsure? Call us on 09617 66 11 66.'}</p>
            </div>
            <div className="acc rv" style={{ ['--d' as string]: '.1s' }}>
              {faqs.map((f) => (
                <details key={f.question}>
                  <summary>
                    {f.question} <i />
                  </summary>
                  <div className="body">
                    <div>{f.answer}</div>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {on('trust') && (
        <section className="sec" style={{ paddingBlock: 72, borderTop: '1px solid var(--line)' }}>
          <div className="wrap trust">
            {TRUST.map(([d, label], i) => (
              <div key={label} className="rv" style={{ ['--d' as string]: `${i * 0.06}s` }}>
                <Svg d={d} />
                {label}
              </div>
            ))}
          </div>
        </section>
      )}

      {host.credit && <p className="credit">{host.credit}</p>}

      <div className={`toast${toast ? ' on' : ''}`} role="status">
        {toast}
      </div>

      {/* Sticky purchase bar */}
      <div className={`sbar${sbar ? ' on' : ''}`}>
        <div className="wrap">
          <img src={product.image} alt="" />
          <div className="t">
            <b>{name}</b>
            <small>
              {isSet
                ? ['Gift box', routineSet?.savings && `Save ${routineSet.savings}`].filter(Boolean).join(' · ')
                : [STEP_TAG[product.stepKeyword]?.split(' · ')[0].replace('STEP', 'Step'), product.volume.replace('ml', ' ml')].filter(Boolean).join(' · ')}
            </small>
          </div>
          <b style={{ font: '500 18px var(--serif)' }}>{product.price}</b>
          <button type="button" className="btn wine" onClick={buyNow} disabled={outOfStock}>
            {t('common.buyNow', 'Buy now')} · COD
          </button>
        </div>
      </div>
    </div>
  );
}
